import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../lib/api-client';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { CheckIcon, LinkIcon, MailIcon } from './icons';

/**
 * Asking for a password reset link.
 *
 * The confirmation is deliberately conditional — "if this address has an account" — because the
 * endpoint answers the same way either way, and a screen that said "sent!" would undo that and turn
 * the form into a way of checking who is a customer.
 */
export function ForgotPasswordPage(): React.JSX.Element {
  const emailId = useId();

  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await apiRequest('/v1/auth/password/forgot', { method: 'POST', body: { email } });
      setSentTo(email);
    } catch {
      setError(t('error.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  if (sentTo) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <span className="auth__done-mark" aria-hidden="true">
            <CheckIcon />
          </span>

          <h1>{t('forgot.sentTitle')}</h1>
          <p>{t('forgot.sentBody').replace('{email}', sentTo)}</p>
          <p className="auth__fineprint">{t('forgot.sentFineprint')}</p>

          <Link className="button button--primary" to="/entrar">
            {t('forgot.backToLogin')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="auth__heading">
        <h1>{t('forgot.title')}</h1>
        <p>{t('forgot.subtitle')}</p>
      </header>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="input">
          <label htmlFor={emailId}>{t('field.email')}</label>
          <div className="input__control">
            <MailIcon />
            <input
              id={emailId}
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting ? t('forgot.submitting') : t('forgot.submit')}
          {!submitting && <LinkIcon />}
        </button>
      </form>

      <p className="auth__switch">
        <Link to="/entrar">{t('forgot.backToLogin')}</Link>
      </p>
    </AuthLayout>
  );
}
